/**
 * What "Clear .env secrets" did beyond the count, worded for the admin
 * (STORY-355). The backend answers per file — the chat service's .env and
 * the MCP server's — with a status and, when the MCP file was left alone,
 * a reason code; plus the backup copies it could not clear (STORY-353) and
 * the placeholders of an earlier purge it kept.
 */
import type { Translation } from '@/i18n';

export interface EnvFileOutcome {
  service: string;
  path: string;
  found: boolean;
  status: 'cleared' | 'left_unchanged' | 'failed' | 'not_found';
  reason?: 'mcp_vault_not_enabled' | 'mcp_unreachable';
  cleared_keys: string[];
}

export interface ClearEnvSecretsResult {
  status: string;
  cleared_keys: string[];
  message: string;
  env_files?: EnvFileOutcome[];
  unscrubbed_backups?: string[];
  legacy_marker_keys?: string[];
}

type Settings = Translation['settings'];

/**
 * The one-line headline over the notes. "Values are now managed exclusively
 * by Vault" is true only when no file still holds a secret the product
 * reads: when the MCP server's .env was left as is (its own Vault is off, or
 * it could not be reached), the cluster tokens are still in that file — so
 * the headline says only the chat service's secrets moved, and the note
 * below says why.
 *
 * Only the MCP file is ever 'left_unchanged', and only after the chat file
 * was cleared — otherwise the backend answers with an error, not a result —
 * so no check on the chat entry is needed. A 'partial' result (one file
 * failed) keeps today's headline; its note names the file. An older backend
 * sends no env_files and gets today's headline.
 */
export function envClearHeadline(res: ClearEnvSecretsResult, s: Settings): string {
  const mcpKept = (res.env_files ?? []).some((f) => f.status === 'left_unchanged');
  return (mcpKept ? s.envSecretsClearedMcpKept : s.envSecretsCleared)
    .replace('{count}', String(res.cleared_keys?.length ?? 0));
}

export function envClearNotes(res: ClearEnvSecretsResult, s: Settings): string[] {
  const notes: string[] = [];
  for (const f of res.env_files ?? []) {
    if (f.status === 'left_unchanged') {
      const text = f.reason === 'mcp_vault_not_enabled' ? s.envClearMcpVaultOff : s.envClearMcpUnreachable;
      notes.push(text.replace('{path}', f.path));
    } else if (f.status === 'failed') {
      notes.push(s.envClearFileFailed.replace('{path}', f.path));
    } else if (f.status === 'not_found') {
      notes.push(s.envClearFileNotFound.replace('{path}', f.path));
    }
  }
  if (res.unscrubbed_backups?.length) {
    notes.push(s.envClearBackupsLeft.replace('{names}', res.unscrubbed_backups.join(', ')));
  }
  if (res.legacy_marker_keys?.length) {
    notes.push(s.envClearLegacyKept.replace('{keys}', res.legacy_marker_keys.join(', ')));
  }
  return notes;
}
